import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Lock, Mail, Loader2, AlertCircle, Sparkles, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export default function AdminV2Login() {
  const { user, login, loginTwoFa } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 2FA step state.
  const [twofaStep, setTwofaStep] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [code, setCode] = useState('');

  if (user && user !== false) {
    const to = location.state?.from || '/admin';
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.twofaRequired) {
      setPendingToken(res.pendingToken);
      setTwofaStep(true);
      return;
    }
    if (!res.success) setError(res.error || 'Login failed');
  };

  const onSubmitCode = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const res = await loginTwoFa(pendingToken, code);
    setLoading(false);
    if (!res.success) setError(res.error || 'Invalid code');
    // success → user is set, redirect happens via the guard above.
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-violet-950 to-fuchsia-950 px-4 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-25"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, rgba(139,92,246,0.5), transparent 45%), radial-gradient(circle at 80% 75%, rgba(217,70,239,0.4), transparent 45%)',
        }}
      />
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white font-extrabold text-2xl shadow-2xl shadow-fuchsia-500/40 mb-4">
            V
          </div>
          <h1 className="text-3xl font-extrabold text-white">
            Admin <span className="bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">v2</span>
          </h1>
          <p className="text-violet-200 mt-1 text-sm flex items-center justify-center gap-1">
            <Sparkles size={12} /> AI-native content management
          </p>
        </div>

        {!twofaStep ? (
          <form
            onSubmit={onSubmit}
            className="backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl p-8 shadow-2xl shadow-black/30 space-y-5"
            data-testid="admin-v2-login-form"
          >
            <label className="block">
              <span className="text-sm font-semibold text-violet-100 mb-2 flex items-center gap-2">
                <Mail size={14} /> Email
              </span>
              <input
                data-testid="admin-v2-login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 focus:border-fuchsia-400 focus:ring-4 focus:ring-fuchsia-500/20 outline-none text-white placeholder:text-white/30 transition-all"
                placeholder="admin@voipinger.com"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-violet-100 mb-2 flex items-center gap-2">
                <Lock size={14} /> Password
              </span>
              <input
                data-testid="admin-v2-login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 focus:border-fuchsia-400 focus:ring-4 focus:ring-fuchsia-500/20 outline-none text-white placeholder:text-white/30 transition-all"
                placeholder="••••••••"
              />
            </label>

            {error && (
              <div className="flex items-start gap-2 text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-xl px-3 py-2 text-sm">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-v2-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : (
          <form
            onSubmit={onSubmitCode}
            className="backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl p-8 shadow-2xl shadow-black/30 space-y-5"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-violet-500/20 text-violet-200 mb-3">
                <ShieldCheck size={22} />
              </div>
              <h2 className="text-lg font-bold text-white">Two-factor authentication</h2>
              <p className="text-violet-200/80 text-sm mt-1">
                Enter the 6-digit code from your authenticator app (or a backup code).
              </p>
            </div>

            <label className="block">
              <span className="sr-only">Authentication code</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 focus:border-fuchsia-400 focus:ring-4 focus:ring-fuchsia-500/20 outline-none text-white text-center text-2xl tracking-[0.5em] font-mono placeholder:text-white/20 transition-all"
                placeholder="000000"
              />
            </label>

            {error && (
              <div className="flex items-start gap-2 text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-xl px-3 py-2 text-sm">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 transition-all disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
              {loading ? 'Verifying…' : 'Verify & sign in'}
            </button>

            <button
              type="button"
              onClick={() => { setTwofaStep(false); setCode(''); setError(null); }}
              className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-violet-200/70 hover:text-white transition-colors"
            >
              <ArrowLeft size={13} /> Back to password
            </button>
          </form>
        )}

        <p className="text-center mt-4 text-xs text-white/40">
          Secured with optional two-factor authentication.
        </p>
      </div>
    </div>
  );
}
